// Public surface of the v2 (declarative, zod-based) extension runtime. Mirrors the v1 entry points
// (getServiceConfigs / runServiceMethod) plus buildServiceDefinition — the v2 analogue of jsdoc-parser
// that produces the same SharedExtensionDefinition model. Version-agnostic callers go through ../index.ts;
// this module is only reached once a service has been detected as v2.

import { type ServiceConfigsValues } from '../common/constants'
import { FlowrunnerContext, type FlowrunnerContextOptions } from '../common/context'
import type { FilesApi } from '../common/context-files'
import type { ConfigItem } from '../common/types'
import type { SharedExtensionDefinition } from './model/model-types'
import { registry } from './registry'
import type { Logger } from './types'

export type { ConfigItem } from '../common/types'
export type { SharedExtensionDefinition } from './model/model-types'

// The registry and what it holds. `getExtension` is how ../index.ts detects a v2 service; `resetRegistry`
// lets a test harness isolate cases (flowrunner-cli/runtime re-exports it).
export { ExecutionContext } from './context/execution-context'
export { Extension } from './extension'
export { getAllExtensions, getExtension, resetRegistry } from './registry'

// The invocation/result shapes a host may need to type a call against the trigger seams.
export type { PollingInvocation, PollingResult } from './polling-triggers/types'
export type { RealtimeInvocation } from './realtime-triggers/types'
export type { HandlerBag, Logger } from './types'

export interface RunServiceMethodData {
  serviceId: string
  serviceEntryPointPath: string
  configs?: ServiceConfigsValues
  methodName: string
  methodData?: Record<string, unknown>
  methodArgs?: unknown[]
  logger?: Logger
  retrieveNewS3Token: (runtimeKey: string) => Promise<{ filesS3Token: string }>
  // Replaces the file-service client for this invocation. The default talks to the console over HTTP; a
  // host may build its own against different storage (an e2e harness pointing `ctx.files` at a local S3,
  // say) without the runner or the service knowing. Only consulted when the extension declares
  // `usesFileStorage` — the throwing proxy is unaffected.
  createFiles?: (options: FlowrunnerContextOptions) => FilesApi
  executionContext: {
    runtimeKey: string
    accessToken: string
    appId: string
    apiKey: string
    serverURL: string
    flowId: string
    executionId: string
  }
}

export function getServiceConfigs(serviceId: string, serviceEntryPointPath: string): ConfigItem[] {
  return registry.load(serviceId, serviceEntryPointPath).toConfigItems()
}

export function buildServiceDefinition(serviceId: string, serviceEntryPointPath: string): SharedExtensionDefinition {
  return registry.load(serviceId, serviceEntryPointPath).toDefinition()
}

// The stock file-service client: the console's REST API at `{serverURL}/{appId}/{apiKey}/v1/files`.
const defaultCreateFiles = (options: FlowrunnerContextOptions): FilesApi => new FlowrunnerContext(options).Files

export async function runServiceMethod(data: RunServiceMethodData): Promise<unknown> {
  const { serviceId, serviceEntryPointPath, executionContext, retrieveNewS3Token, methodName } = data

  const methodData = data.methodData || {}

  const { serverURL, appId, apiKey, runtimeKey, flowId, executionId, accessToken } = executionContext

  const extension = registry.load(serviceId, serviceEntryPointPath)

  const filesOptions: FlowrunnerContextOptions = {
    serverURL,
    appId,
    apiKey,
    runtimeKey,
    flowId,
    executionId,
    retrieveNewS3Token,
  }

  const files = extension.props.usesFileStorage
    ? (data.createFiles ?? defaultCreateFiles)(filesOptions)
    : undefined

  const ctx = extension.createContext({ configs: data.configs, accessToken, logger: data.logger, files })

  return extension.run(methodName, methodData, ctx)
}
